import * as AlertPrimitive from '@radix-ui/react-alert-dialog'
import { type ComponentProps, type ReactNode, useRef } from 'react'
import { dialogPanelOverlayClassName, dialogSurfaceStyles } from '../lib/dialog-panel-styles'
import { ActionBar, ActionBarGroup } from './action-bar'
import { Alert } from './alert'
import { Button } from './button'
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './dialog'

type Props = ComponentProps<typeof Dialog> & {
  cancelLabel: string
  confirmLabel: string
  description: ReactNode
  heading: ReactNode
  icon?: ReactNode
  error?: ReactNode
  onConfirm: NonNullable<ComponentProps<typeof Button>['onClick']>
  pending?: boolean
  secondaryAction?: {
    disabled?: boolean
    label: string
    onClick: NonNullable<ComponentProps<typeof Button>['onClick']>
    tone?: 'accent' | 'danger' | 'neutral'
  }
  trigger?: ReactNode
  tone?: 'accent' | 'danger'
  onCloseAutoFocus?: ComponentProps<typeof DialogContent>['onCloseAutoFocus']
}
function useConfirmationFocus(onCloseAutoFocus: Props['onCloseAutoFocus']) {
  const previousFocus = useRef<Element | null>(null)
  return {
    onOpenAutoFocus: () => {
      previousFocus.current = document.activeElement
    },
    onCloseAutoFocus: (event: Event) => {
      onCloseAutoFocus?.(event)
      if (
        !event.defaultPrevented &&
        previousFocus.current instanceof HTMLElement &&
        previousFocus.current.isConnected
      ) {
        event.preventDefault()
        previousFocus.current.focus()
      }
    },
  }
}
export function ConfirmationDialog({
  behavior = 'dismissible',
  ...props
}: Props & { behavior?: 'dismissible' | 'explicit' }) {
  return behavior === 'explicit' ? (
    <ExplicitConfirmation {...props} />
  ) : (
    <DismissibleConfirmation {...props} />
  )
}
function DismissibleConfirmation({
  cancelLabel,
  children,
  trigger,
  confirmLabel,
  description,
  heading,
  icon,
  error,
  onConfirm,
  pending,
  secondaryAction,
  tone = 'danger',
  onCloseAutoFocus,
  ...props
}: Props) {
  const focus = useConfirmationFocus(onCloseAutoFocus)
  return (
    <Dialog {...props}>
      {trigger ? <DialogTrigger asChild>{trigger}</DialogTrigger> : null}
      {children}
      <DialogContent showCloseButton={false} width="compact" {...focus}>
        <DialogHeader>
          {icon ? <span aria-hidden="true">{icon}</span> : null}
          <DialogTitle>{heading}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <DialogDescription>{description}</DialogDescription>
          {error ? <Alert>{error}</Alert> : null}
        </DialogBody>
        <DialogFooter>
          <ActionBar>
            <ActionBarGroup>
              <DialogClose asChild>
                <Button appearance="ghost" tone="neutral">
                  {cancelLabel}
                </Button>
              </DialogClose>
              {secondaryAction ? (
                <Button
                  appearance="ghost"
                  tone={secondaryAction.tone ?? 'neutral'}
                  disabled={pending || secondaryAction.disabled}
                  onClick={secondaryAction.onClick}
                >
                  {secondaryAction.label}
                </Button>
              ) : null}
              <Button tone={tone} pending={pending} onClick={onConfirm}>
                {confirmLabel}
              </Button>
            </ActionBarGroup>
          </ActionBar>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
function ExplicitConfirmation({
  cancelLabel,
  children,
  trigger,
  confirmLabel,
  description,
  heading,
  icon,
  error,
  onConfirm,
  pending,
  secondaryAction,
  tone = 'danger',
  onCloseAutoFocus,
  ...props
}: Props) {
  const focus = useConfirmationFocus(onCloseAutoFocus)
  return (
    <AlertPrimitive.Root {...props}>
      {trigger ? <AlertPrimitive.Trigger asChild>{trigger}</AlertPrimitive.Trigger> : null}
      {children}
      <AlertPrimitive.Portal>
        <AlertPrimitive.Overlay className={dialogPanelOverlayClassName} />
        <AlertPrimitive.Content {...focus} className={dialogSurfaceStyles('compact')}>
          <DialogHeader>
            {icon ? <span aria-hidden="true">{icon}</span> : null}
            <AlertPrimitive.Title className="font-heading text-2xl">{heading}</AlertPrimitive.Title>
          </DialogHeader>
          <DialogBody>
            <AlertPrimitive.Description className="text-sm leading-6 text-muted-foreground">
              {description}
            </AlertPrimitive.Description>
            {error ? <Alert>{error}</Alert> : null}
          </DialogBody>
          <DialogFooter>
            <ActionBar>
              <ActionBarGroup>
                <AlertPrimitive.Cancel asChild>
                  <Button appearance="ghost" tone="neutral">
                    {cancelLabel}
                  </Button>
                </AlertPrimitive.Cancel>
                {secondaryAction ? (
                  <Button
                    appearance="ghost"
                    tone={secondaryAction.tone ?? 'neutral'}
                    disabled={pending || secondaryAction.disabled}
                    onClick={secondaryAction.onClick}
                  >
                    {secondaryAction.label}
                  </Button>
                ) : null}
                <Button tone={tone} pending={pending} onClick={onConfirm}>
                  {confirmLabel}
                </Button>
              </ActionBarGroup>
            </ActionBar>
          </DialogFooter>
        </AlertPrimitive.Content>
      </AlertPrimitive.Portal>
    </AlertPrimitive.Root>
  )
}
